import { lazy } from "react";
import { FC } from "react";
import { useLayoutEffect } from "react";
import { TranslationText } from "components";
import { NavTabRoutes } from "layouts/NavTabLayout/NavTab/NavTabRoutes";
import { NavTabPath } from "layouts/NavTabLayout/NavTab/types";
import { PortfolioNavigationHeaderLayout } from "layouts/PortfolioNavigationHeaderLayout/PortfolioNavigationHeaderLayout";
import { useTranslation } from "react-i18next";
import { Navigate, useRoutes } from "react-router-dom";
import { useGetContactInfo } from "../../../../src/api/initial/useGetContactInfo";
import { useTradablePortfolioSelect } from "../../../../src/components/TradingModals/useTradablePortfolioSelect";
import { isPortfolioAllowedToTransfer } from "../../../../src/services/permissions/transfer";
import { useGetContactIdData } from "../../../providers/ContactIdProvider";

interface ExternalRedirectProps {
  to: string;
}

const languageUrlMap = {
  "en-US": "https://www.kvarnx.com/en/how-to-use",
  "fi-FI": "https://www.kvarnx.com/palvelun-kayttoohje",
  "sv-SE": "https://www.kvarnx.com/en/how-to-use",
  // add more languages if needed
};

const ExternalRedirect: FC<ExternalRedirectProps> = ({ to }) => {
  const { i18n } = useTranslation();
  const url =
    languageUrlMap[i18n.language as keyof typeof languageUrlMap] || to;

  useLayoutEffect(() => {
    const newWindow = window.open(url, "_blank");
    if (newWindow) newWindow.opener = null;
  }, [url]);

  return <Navigate to="/overview" replace />;
};

const Overview = lazy(() =>
  import("./overview").then((module) => ({ default: module.OverviewPage }))
);
const Trading = lazy(() =>
  import("./trading").then((module) => ({ default: module.TradingPage }))
);
const Holdings = lazy(() =>
  import("./holdings").then((module) => ({ default: module.HoldingsView }))
);
const Holding = lazy(() =>
  import("./holdings/[holdingId]").then((module) => ({
    default: module.HoldingPage,
  }))
);
const Transactions = lazy(() =>
  import("./transactions").then((module) => ({
    default: module.TransactionsPage,
  }))
);
const TransactionDetails = lazy(() =>
  import("./transactions/[transactionId]").then((module) => ({
    default: module.TransactionDetailsPage,
  }))
);
const OrderDetails = lazy(() =>
  import("./orders/[orderId]").then((module) => ({
    default: module.OrderDetailsPage,
  }))
);
const Documents = lazy(() =>
  import("./documents").then((module) => ({ default: module.DocumentsPage }))
);

const Transfers = lazy(() =>
  import("./transfers").then((module) => ({ default: module.TransferPage }))
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const portfolioTabRoutes = (foundPortfolio: any): NavTabPath[] => [
  {
    path: "overview",
    tabLabel: <TranslationText translationKey="navTab.tabs.overview" />,
    tabComponent: <Overview />,
    element: null,
  },
  {
    path: "trading",
    tabLabel: <TranslationText translationKey="navTab.tabs.trading" />,
    tabComponent: <Trading />,
    element: null,
  },
  {
    path: "holdings",
    tabLabel: <TranslationText translationKey="navTab.tabs.holdings" />,
    tabComponent: <Holdings />,
    element: null,
  },
  {
    path: "transactions",
    tabLabel: <TranslationText translationKey="navTab.tabs.transactions" />,
    tabComponent: <Transactions />,
    element: null,
  },
  {
    path: "documents",
    tabLabel: <TranslationText translationKey="navTab.tabs.documents" />,
    tabComponent: <Documents />,
    element: null,
  },
  ...(isPortfolioAllowedToTransfer(foundPortfolio)
    ? [
        {
          path: "transfers",
          tabLabel: <TranslationText translationKey="navTab.tabs.transfers" />,
          tabComponent: <Transfers />,
          element: null,
        },
      ]
    : []),
  {
    path: "external",
    tabLabel: <TranslationText translationKey="navTab.tabs.manual" />,
    tabComponent: <div>External Link</div>,
    element: (
      <ExternalRedirect to="https://www.kvarnx.com/palvelun-kayttoohje" />
    ),
  },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const portfolioRoutes = (foundPortfolio: any) => [
  {
    path: "",
    element: <Navigate to="overview" replace />,
  },
  {
    path: "",
    element: <PortfolioNavigationHeaderLayout />,
    children: [
      {
        path: "*",
        element: <NavTabRoutes routes={portfolioTabRoutes(foundPortfolio)} />,
      },
    ],
  },
  {
    path: "holdings/:holdingId",
    element: <Holding />,
  },
  {
    path: "transactions/:transactionId",
    element: <TransactionDetails />,
  },
  {
    path: "orders/:orderId",
    element: <OrderDetails />,
  },
];

export const PortfolioRoutes = () => {
  const { selectedContactId } = useGetContactIdData();
  const { data: { portfolios } = { portfolios: [] } } = useGetContactInfo(
    false,
    selectedContactId
  );
  const { portfolioId } = useTradablePortfolioSelect();

  const foundPortfolio = portfolios.find(
    (portfolio) => portfolio.id === portfolioId
  );

  const routes = portfolioRoutes(foundPortfolio);
  return useRoutes(routes);
};
